import { useId, CSSProperties } from 'react'

interface TubeData {
  id: string
  fillLevel: number // 0-100
  fillColor: string
  label?: string
}

interface TestTubeRackProps {
  tubes?: TubeData[]
  onTubeClick?: (tubeId: string) => void
  disabled?: boolean
  className?: string
}

const TestTubeRack = ({
  tubes = [],
  onTubeClick,
  disabled = false,
  className = ''
}: TestTubeRackProps) => {
  const id = useId()

  // Default to 6 empty slots if no tubes provided
  const maxTubes = 6
  const tubeSlots = Array.from({ length: maxTubes }, (_, i) => {
    return tubes.find((t, idx) => idx === i) || null
  })

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  return (
    <svg
      viewBox="0 0 180 120"
      width={170}
      height={115}
      style={containerStyle}
      className={className}
    >
      <defs>
        {/* Wood grain gradient */}
        <linearGradient id={`wood-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#8B6914" />
          <stop offset="20%" stopColor="#A67B1A" />
          <stop offset="50%" stopColor="#8B6914" />
          <stop offset="80%" stopColor="#7A5A10" />
          <stop offset="100%" stopColor="#6B4D0E" />
        </linearGradient>

        {/* Wood side gradient */}
        <linearGradient id={`wood-side-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#6B4D0E" />
          <stop offset="50%" stopColor="#7A5A10" />
          <stop offset="100%" stopColor="#5A4008" />
        </linearGradient>

        {/* Glass gradient for tubes */}
        <linearGradient id={`glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
          <stop offset="20%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="50%" stopColor="rgba(200,220,240,0.3)" />
          <stop offset="80%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
        </linearGradient>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="2" dy="3" stdDeviation="2" floodOpacity="0.3" />
        </filter>

        {/* Liquid gradients for each tube */}
        {tubeSlots.map((tube, i) => tube && (
          <linearGradient key={i} id={`liquid-${id}-${i}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={tube.fillColor} stopOpacity="0.85" />
            <stop offset="100%" stopColor={tube.fillColor} stopOpacity="0.95" />
          </linearGradient>
        ))}
      </defs>

      <g filter={`url(#shadow-${id})`}>
        {/* Back support bar */}
        <rect
          x="10"
          y="15"
          width="160"
          height="8"
          fill={`url(#wood-${id})`}
          rx="2"
        />

        {/* Left vertical support */}
        <rect
          x="10"
          y="15"
          width="8"
          height="85"
          fill={`url(#wood-side-${id})`}
          rx="2"
        />

        {/* Right vertical support */}
        <rect
          x="162"
          y="15"
          width="8"
          height="85"
          fill={`url(#wood-side-${id})`}
          rx="2"
        />

        {/* Top holder bar with holes */}
        <rect
          x="10"
          y="25"
          width="160"
          height="12"
          fill={`url(#wood-${id})`}
          rx="2"
        />

        {/* Middle support bar */}
        <rect
          x="10"
          y="60"
          width="160"
          height="8"
          fill={`url(#wood-${id})`}
          rx="2"
        />

        {/* Bottom base */}
        <rect
          x="5"
          y="95"
          width="170"
          height="10"
          fill={`url(#wood-${id})`}
          rx="3"
        />

        {/* Feet */}
        <rect x="15" y="102" width="15" height="6" fill="#5A4008" rx="2" />
        <rect x="150" y="102" width="15" height="6" fill="#5A4008" rx="2" />
      </g>

      {/* Tube holes in top bar */}
      {tubeSlots.map((_, i) => (
        <ellipse
          key={`hole-${i}`}
          cx={35 + i * 22}
          cy="31"
          rx="8"
          ry="4"
          fill="#4A3A10"
        />
      ))}

      {/* Test tubes */}
      {tubeSlots.map((tube, i) => {
        const tubeX = 35 + i * 22
        const tubeStyle: CSSProperties = {
          cursor: disabled ? 'default' : tube && onTubeClick ? 'pointer' : 'default',
          transition: 'transform 0.2s ease'
        }

        return (
          <g
            key={`tube-${i}`}
            style={tubeStyle}
            onClick={!disabled && tube && onTubeClick ? () => onTubeClick(tube.id) : undefined}
          >
            {tube ? (
              <>
                {/* Test tube rim */}
                <ellipse
                  cx={tubeX}
                  cy="28"
                  rx="7"
                  ry="3"
                  fill="rgba(200,220,240,0.5)"
                  stroke="#8BA8C4"
                  strokeWidth="1"
                />

                {/* Test tube body */}
                <path
                  d={`M${tubeX - 6} 28 L${tubeX - 6} 75 Q${tubeX - 6} 85 ${tubeX} 85 Q${tubeX + 6} 85 ${tubeX + 6} 75 L${tubeX + 6} 28`}
                  fill={`url(#glass-${id})`}
                  stroke="#8BA8C4"
                  strokeWidth="1"
                />

                {/* Liquid fill */}
                {tube.fillLevel > 0 && (
                  <path
                    d={`M${tubeX - 5} ${83 - tube.fillLevel * 0.5} L${tubeX - 5} 73 Q${tubeX - 5} 82 ${tubeX} 82 Q${tubeX + 5} 82 ${tubeX + 5} 73 L${tubeX + 5} ${83 - tube.fillLevel * 0.5} Z`}
                    fill={`url(#liquid-${id}-${i})`}
                  />
                )}

                {/* Glass shine */}
                <line
                  x1={tubeX - 4}
                  y1="32"
                  x2={tubeX - 4}
                  y2="70"
                  stroke="rgba(255,255,255,0.5)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />

                {/* Label */}
                {tube.label && (
                  <text
                    x={tubeX}
                    y="95"
                    fontSize="7"
                    fill="#E0E0E0"
                    textAnchor="middle"
                    fontWeight="500"
                  >
                    {tube.label}
                  </text>
                )}
              </>
            ) : (
              /* Empty slot indicator */
              <ellipse
                cx={tubeX}
                cy="31"
                rx="6"
                ry="3"
                fill="#3A2A08"
                opacity="0.5"
              />
            )}
          </g>
        )
      })}

      {/* Label */}
      <text
        x="90"
        y="115"
        fontSize="9"
        fill="#E0E0E0"
        textAnchor="middle"
        fontWeight="500"
      >
        Test Tube Rack
      </text>
    </svg>
  )
}

export default TestTubeRack
